import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaTimes, FaHeart, FaUserPlus, FaUserCheck, FaClock } from "react-icons/fa";
import Modal from "./Modal";
import getImageUrl from "../../utils/imageUtils";
import api from "../../utils/api/axiosInstance";
import { useUserAuth } from "../../contexts/UserAuthContext";
import styles from "../../styles/components/common/LikesModal.module.css";

const LikesModal = ({ isOpen, onClose, postId }) => {
  const { user: currentUser } = useUserAuth();
  const [likes, setLikes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [followingMap, setFollowingMap] = useState(new Map());
  const [followLoading, setFollowLoading] = useState(new Set());
  const [pendingRequests, setPendingRequests] = useState(new Set());

  useEffect(() => {
    if (isOpen && postId) {
      fetchLikes();
    }
  }, [isOpen, postId]);

  useEffect(() => {
    if (likes.length > 0) {
      const userIds = likes.map(user => user._id);
      checkFollowingStatus(userIds);
    }
  }, [likes]);

  const fetchLikes = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/posts/${postId}/likes`);
      setLikes(response.data.likes || []);
    } catch (err) {
      console.error("Error fetching likes:", err);
      setError(err.response?.data?.message || "Failed to load likes");
    } finally {
      setLoading(false);
    }
  };

  const checkFollowingStatus = async (userIds) => {
    if (!currentUser || userIds.length === 0) return;

    try {
      // Get current user's following list
      const response = await api.get(`/users/me/following`);
      const following = response.data.following || [];
      const followingIds = new Set(following.map(user => user._id));

      // Check follow status for each liked user
      const newFollowingMap = new Map();
      const newPendingRequests = new Set();

      for (const userId of userIds) {
        if (followingIds.has(userId)) {
          newFollowingMap.set(userId, true);
        } else {
          // Check if there's a pending follow request
          try {
            const followStatusResponse = await api.get(`/users/${userId}/follow-status`);
            if (followStatusResponse.data.hasPendingRequest) {
              newPendingRequests.add(userId);
            }
          } catch (err) {
            // Ignore errors for individual follow status checks
            console.log(`Could not check follow status for user ${userId}:`, err);
          }
        }
      }

      setFollowingMap(newFollowingMap);
      setPendingRequests(newPendingRequests);
    } catch (err) {
      console.error("Error checking following status:", err);
    }
  };

  const handleClose = () => {
    setLikes([]);
    setError(null);
    setFollowingMap(new Map());
    setPendingRequests(new Set());
    onClose();
  };

  const handleFollow = async (targetUserId) => {
    if (!currentUser) {
      return;
    }

    // Optimistically update UI
    const previousFollowing = followingMap.get(targetUserId) || false;
    const previousPending = pendingRequests.has(targetUserId);

    setFollowLoading(prev => new Set(prev).add(targetUserId));

    try {
      const res = await api.post(`/users/${targetUserId}/follow`);

      // Update following map and pending requests based on server response
      if (res.data.isFollowing) {
        setFollowingMap(prev => new Map(prev).set(targetUserId, true));
        setPendingRequests(prev => {
          const newSet = new Set(prev);
          newSet.delete(targetUserId);
          return newSet;
        });
      } else if (res.data.hasPendingRequest) {
        setPendingRequests(prev => new Set(prev).add(targetUserId));
        setFollowingMap(prev => {
          const newMap = new Map(prev);
          newMap.delete(targetUserId);
          return newMap;
        });
      } else {
        setFollowingMap(prev => {
          const newMap = new Map(prev);
          newMap.delete(targetUserId);
          return newMap;
        });
        setPendingRequests(prev => {
          const newSet = new Set(prev);
          newSet.delete(targetUserId);
          return newSet;
        });
      }

    } catch (err) {
      console.error("Error following user:", err);
      // Revert optimistic updates on error
      if (previousFollowing) {
        setFollowingMap(prev => new Map(prev).set(targetUserId, true));
      } else {
        setFollowingMap(prev => {
          const newMap = new Map(prev);
          newMap.delete(targetUserId);
          return newMap;
        });
      }
      if (previousPending) {
        setPendingRequests(prev => new Set(prev).add(targetUserId));
      } else {
        setPendingRequests(prev => {
          const newSet = new Set(prev);
          newSet.delete(targetUserId);
          return newSet;
        });
      }
    } finally {
      setFollowLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(targetUserId);
        return newSet;
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} className={styles.likesModal}>
      <div className={styles.modalHeader}>
        <h2 className={styles.modalTitle}>
          <FaHeart className={styles.heartIcon} />
          Likes ({likes.length})
        </h2>
      </div>

      <div className={styles.modalContent}>
        {loading && (
          <div className={styles.loading}>
            <div className={styles.spinner}></div>
            <p>Loading likes...</p>
          </div>
        )}

        {error && (
          <div className={styles.error}>
            <p>{error}</p>
            <button
              type="button"
              className={styles.retryButton}
              onClick={fetchLikes}
            >
              Try Again
            </button>
          </div>
        )}

        {!loading && !error && likes.length === 0 && (
          <div className={styles.empty}>
            <FaHeart className={styles.emptyIcon} />
            <p>No likes yet</p>
          </div>
        )}

        {!loading && !error && likes.length > 0 && (
          <div className={styles.likesList}>
            {likes
              .sort((a, b) => {
                const isCurrentUserA = currentUser && (currentUser.id === a._id || currentUser._id === a._id);
                const isCurrentUserB = currentUser && (currentUser.id === b._id || currentUser._id === b._id);
                if (isCurrentUserA && !isCurrentUserB) return -1;
                if (!isCurrentUserA && isCurrentUserB) return 1;
                return 0;
              })
              .map((user) => {
              const isFollowing = followingMap.get(user._id) || false;
              const hasPendingRequest = pendingRequests.has(user._id);
              const isLoading = followLoading.has(user._id);
              const isCurrentUser = currentUser && (currentUser.id === user._id || currentUser._id === user._id);

              return (
                <div key={user._id} className={styles.userItem}>
                  <Link
                    to={`/user/${user._id}`}
                    className={styles.userLink}
                    onClick={handleClose}
                  >
                    <div className={styles.userAvatar}>
                      {user.avatar ? (
                        <img
                          src={getImageUrl(user.avatar)}
                          alt={`${user.firstName} ${user.lastName}`}
                          className={styles.avatarImage}
                        />
                      ) : (
                        <div className={styles.avatarPlaceholder}>
                          {user.firstName?.[0] || "U"}
                        </div>
                      )}
                    </div>
                    <div className={styles.userInfo}>
                      <div className={styles.userName}>
                        {user.firstName} {user.lastName}
                      </div>
                      {user.username && (
                        <div className={styles.userUsername}>@{user.username}</div>
                      )}
                    </div>
                  </Link>
                  {currentUser && !isCurrentUser && !isFollowing && !hasPendingRequest && (
                    <button
                      type="button"
                      className={styles.followButton}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleFollow(user._id);
                      }}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <div className={styles.followLoading}></div>
                      ) : (
                        <>
                          <FaUserPlus />
                          Follow
                        </>
                      )}
                    </button>
                  )}
                  {currentUser && !isCurrentUser && isFollowing && (
                    <button
                      type="button"
                      className={`${styles.followButton} ${styles.following}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleFollow(user._id);
                      }}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <div className={styles.followLoading}></div>
                      ) : (
                        <>
                          <FaUserCheck />
                          Unfollow
                        </>
                      )}
                    </button>
                  )}
                  {currentUser && !isCurrentUser && hasPendingRequest && (
                    <button
                      type="button"
                      className={`${styles.followButton} ${styles.pending}`}
                      disabled={true}
                    >
                      <FaClock />
                      Pending
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default LikesModal;